<template>
  <div class="card agreement-card">
    <div class="card-header">
      <h3 class="card-title">{{ agreement.name }}</h3>
      <v-chip
        v-if="agreement.is_active"
        small
        color="success"
        text-color="white"
        class="badge"
      >
        {{ $t('is_active') }}
      </v-chip>
      <v-chip
        v-else
        small
        color="grey"
        text-color="white"
        class="badge"
      >
        {{ $t('is_inactive') }}
      </v-chip>
    </div>

    <v-divider></v-divider>

    <div class="card-content">
      <div class="field-group">
        <label class="field-label">{{ $t('agreement_type') }}</label>
        <span class="field-value" :class="getTypeColorClass()">
          {{ getAgreementTypeName(agreement.agreement_type) }}
        </span>
      </div>

      <div class="field-group">
        <label class="field-label">{{ $t('field_location') }}</label>
        <span class="field-value">{{ agreement.location }}</span>
      </div>

      <div class="field-group">
        <label class="field-label">{{ $t('field_phone') }}</label>
        <a :href="`tel:${agreement.phone}`" class="field-link">
          {{ agreement.phone }}
        </a>
      </div>

      <v-divider class="my-3"></v-divider>

      <div class="coordinator-section">
        <h4 class="coordinator-title">{{ $t('coordinator') }}</h4>

        <div class="field-group">
          <label class="field-label">{{ $t('coordinator_name') }}</label>
          <span class="field-value font-weight-bold">{{ agreement.coordinator_name }}</span>
        </div>

        <div class="field-group">
          <label class="field-label">{{ $t('coordinator_email') }}</label>
          <a :href="`mailto:${agreement.coordinator_email}`" class="field-link">
            {{ agreement.coordinator_email }}
          </a>
        </div>

        <div class="field-group">
          <label class="field-label">{{ $t('coordinator_phone') }}</label>
          <a :href="`tel:${agreement.coordinator_phone}`" class="field-link">
            {{ agreement.coordinator_phone }}
          </a>
        </div>
      </div>

      <v-divider class="my-3"></v-divider>

      <div class="validity-section">
        <small class="validity-text">
          <strong>{{ $t('field_validity') }}:</strong>
          {{ formatDate(agreement.valid_from) }} - {{ formatDate(agreement.valid_until) }}
        </small>
      </div>
    </div>

    <v-divider></v-divider>

    <div class="card-actions">
      <v-btn
        v-if="showEdit"
        small
        outlined
        color="primary"
        @click="$emit('edit')"
        class="mr-2"
      >
        {{ $t('modificar') }}
      </v-btn>
      <v-btn
        v-if="showDelete"
        small
        outlined
        color="error"
        @click="$emit('delete')"
      >
        {{ $t('eliminar') }}
      </v-btn>
      <v-btn
        v-if="agreement.is_active && !showEdit"
        small
        color="primary"
        @click="$emit('contact', agreement.coordinator_email)"
      >
        {{ $t('coordinator_contact') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'CourseAgreementCard',
  props: {
    agreement: {
      type: Object,
      required: true
    },
    showEdit: {
      type: Boolean,
      default: false
    },
    showDelete: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('courseAgreements', ['getAgreementTypeName'])
  },
  methods: {
    formatDate (date) {
      if (!date) return '--'
      return new Date(date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },
    getTypeColorClass () {
      const typeId = this.agreement.agreement_type
      const colors = {
        1: 'type-pago',
        2: 'type-cortesia',
        3: 'type-intercambio',
        4: 'type-privilegio',
        5: 'type-especial'
      }
      return colors[typeId] || ''
    }
  }
}
</script>

<style scoped lang="scss">
.agreement-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.card-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
}

.badge {
  white-space: nowrap;
}

.card-content {
  padding: 16px;
}

.field-group {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.field-value {
  display: block;
  color: #333;
  font-size: 14px;

  &.type-pago {
    color: #d32f2f;
    font-weight: 600;
  }

  &.type-cortesia {
    color: #388e3c;
    font-weight: 600;
  }

  &.type-intercambio {
    color: #f57c00;
    font-weight: 600;
  }

  &.type-privilegio {
    color: #0288d1;
    font-weight: 600;
  }

  &.type-especial {
    color: #7b1fa2;
    font-weight: 600;
  }
}

.field-link {
  color: #0066cc;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: #0052a3;
    text-decoration: underline;
  }
}

.coordinator-section {
  .coordinator-title {
    margin: 0 0 12px 0;
    color: #333;
    font-size: 14px;
    font-weight: 600;
  }
}

.validity-section {
  margin-top: 8px;

  .validity-text {
    color: #999;
  }
}

.card-actions {
  padding: 12px 16px;
  background: #f9f9f9;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
